import React, { useEffect, useState } from "react";
import { FaDownload, FaTimes } from "react-icons/fa";
import "../assets/css/InstallPwaBanner.css";

const DISMISS_KEY = "cloud_office_pwa_install_dismissed";

function isStandaloneDisplay() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia("(display-mode: standalone)").matches) return true;
  if (window.navigator.standalone === true) return true;
  return false;
}

/**
 * Captures beforeinstallprompt and shows a dismissible install CTA.
 * Chrome/Edge/Android only; iOS uses Share → Add to Home Screen (no event).
 */
function InstallPwaBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [updateRegistration, setUpdateRegistration] = useState(null);

  useEffect(() => {
    if (isStandaloneDisplay()) return undefined;

    let dismissed = false;
    try {
      dismissed = localStorage.getItem(DISMISS_KEY) === "1";
    } catch (_) {
      /* ignore */
    }

    const onBeforeInstall = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      if (!dismissed) setVisible(true);
    };

    const onInstalled = () => {
      setDeferredPrompt(null);
      setVisible(false);
    };

    const onPwaUpdate = (event) => {
      if (event?.detail?.registration) {
        setUpdateRegistration(event.detail.registration);
      }
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("cloud-office-pwa-update", onPwaUpdate);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("cloud-office-pwa-update", onPwaUpdate);
    };
  }, []);

  const dismissInstall = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch (_) {
      /* ignore */
    }
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch (_) {
      /* ignore */
    }
    setDeferredPrompt(null);
    setVisible(false);
  };

  const handleReloadUpdate = () => {
    const registration = updateRegistration;
    if (registration?.waiting) {
      registration.waiting.postMessage({ type: "SKIP_WAITING" });
    }
    setUpdateRegistration(null);
    window.location.reload();
  };

  if (updateRegistration) {
    return (
      <div className="pwa-banner pwa-banner--update" role="status">
        <p className="pwa-banner__text">A new version of Cloud Office is ready.</p>
        <div className="pwa-banner__actions">
          <button type="button" className="pwa-banner__btn pwa-banner__btn--primary" onClick={handleReloadUpdate}>
            Refresh
          </button>
          <button
            type="button"
            className="pwa-banner__btn pwa-banner__btn--ghost"
            onClick={() => setUpdateRegistration(null)}
            aria-label="Dismiss update notice"
          >
            <FaTimes aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  if (!visible || !deferredPrompt) return null;

  return (
    <div className="pwa-banner" role="dialog" aria-label="Install Cloud Office">
      <div className="pwa-banner__copy">
        <p className="pwa-banner__title">Install Cloud Office</p>
        <p className="pwa-banner__text">Add it to your home screen for quick access.</p>
      </div>
      <div className="pwa-banner__actions">
        <button type="button" className="pwa-banner__btn pwa-banner__btn--primary" onClick={handleInstall}>
          <FaDownload aria-hidden /> Install
        </button>
        <button type="button" className="pwa-banner__btn pwa-banner__btn--ghost" onClick={dismissInstall} aria-label="Dismiss">
          <FaTimes aria-hidden />
        </button>
      </div>
    </div>
  );
}

export default InstallPwaBanner;
